import React, { Component, Fragment } from 'react';
import { compose } from 'redux';
import PropTypes from 'prop-types';
import { reduxForm, SubmissionError } from 'redux-form';
import { connect } from 'react-redux';
import { graphql } from 'react-apollo';

import { banner } from 'actions';
import { Button, Input, IconList, ActionList } from 'common/components';
import { graphQlToFormErrors, capitalizeString } from 'common/utils';
import { required, numberOnly } from 'common/form-validators';
import { limitDigits, onlyNumbers } from 'common/normalizers';
import { toCommaNumber, date } from 'common/formatters';
import { BANNER_IDS, ERROR_DEFAULT, ANALYTICS } from 'common/constants';
import { MUTATION_UPDATE_CROP, FETCH_CROP } from 'api/crop';
import mixpanel from 'mixpanel-browser';
// import { FETCH_FIELD_DETAILS_WITH_NAME } from 'api/field';

export class YieldForm extends Component {
  normalizeFarmSize = value => limitDigits(onlyNumbers(value), 7, 2);

  render() {
    const {
      onSubmit,
      closeForm,
      error,
      handleSubmit,
      valid,
      pristine,
      submitting,
      crop,
      loading,
    } = this.props;
    const associated = crop && [
      {
        label: `${crop.crop.name} ${
          crop.crop.variety ? crop.crop.variety : ''
        }`,
        icon: 'crop',
      },
    ];
    const list =
      crop &&
      (crop.expectedYield || [])
        .map(
          expectedYield =>
            expectedYield && {
              label: `${date(expectedYield.timestamp)} - ${toCommaNumber(
                expectedYield.yield.size
              )} ${capitalizeString(
                expectedYield.yield.type
              )}/${capitalizeString(expectedYield.yield.per)}`,
              icon: 'calendar',
              harvested: expectedYield.yield.harvested,
            }
        )
        .reverse();
    const isHarvested = list && list.length && list[0].harvested;

    return (
      <form onSubmit={handleSubmit(onSubmit)}>
        <h2 className={'text--header'}>Yield History</h2>
        <small className={'text--info'}>* Required</small>

        {associated && <IconList list={associated} />}

        {isHarvested && (
          <ActionList
            className="field"
            cards={[
              {
                content: (
                  <Fragment>
                    <h4 className="text--labelheader">Actual Yield</h4>
                    <IconList list={[list[0]]} theme={['strong']} />
                  </Fragment>
                ),
              },
            ]}
          />
        )}

        {loading ? (
          <p>Loading...</p>
        ) : (
          list && (
            <Fragment>
              <h4 className="text--labelheader">Expected Yield</h4>
              <IconList list={list} />
            </Fragment>
          )
        )}

        {error && <p className="text--error">{error}</p>}

        {!isHarvested && (
          <Fragment>
            <div className={'field__group'}>
              <Input
                name="expectedYield"
                disabled={submitting}
                title="New Expected Yield"
                infoText="Bushels/Acre"
                helpText="This will effect your yield and revenue calculations on the dashboard."
                validate={[required, numberOnly]}
                normalize={this.normalizeFarmSize}
                format={toCommaNumber}
              />
            </div>

            <div className={'field__group'}>
              <fieldset className={'field text--center'}>
                <Button
                  type="submit"
                  className="btn btn--primary gap-top-small"
                  disabled={!valid || pristine || submitting}
                >
                  Save
                </Button>
              </fieldset>

              <fieldset className={'field text--center'}>
                <Button
                  type="cancel"
                  className="btn"
                  onClick={closeForm}
                  disabled={submitting}
                >
                  Cancel
                </Button>
              </fieldset>
            </div>
          </Fragment>
        )}
      </form>
    );
  }
}

YieldForm.propTypes = {
  // redux form
  error: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  valid: PropTypes.bool.isRequired,
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  // map dispatch
  onSubmit: PropTypes.func.isRequired,

  // manually added
  closeForm: PropTypes.func.isRequired,
  id: PropTypes.string.isRequired,

  // from graphQL
  loading: PropTypes.bool,
  crop: PropTypes.object,
};

const formSettings = { form: 'yieldform', enableReinitialize: true };
const mapDispatch = (dispatch, props) => ({
  onSubmit: async values => {
    let vars = {
      expectedYield: values.expectedYield
        ? {
            size: values.expectedYield,
            type: 'bushels', // BE has default values too
            per: 'acres', // BE has default values too
          }
        : null, // null can be saved after BE update
    };

    const res = await props.onUpdateCrop(props.id, vars);

    if (res && res.data && res.data.updateCropForField) {
      props.closeForm();
      dispatch(banner.display(BANNER_IDS.APP, 'YIELD UPDATED', false));
    } else if (res && res.errors) {
      const _error = res.errors[0] ? res.errors[0].message : ERROR_DEFAULT;
      throw new SubmissionError({ _error });
    } else {
      throw new SubmissionError({ _error: ERROR_DEFAULT });
    }
  },
});

export default compose(
  graphql(FETCH_CROP, {
    options: props => ({
      variables: { id: props.id },
      fetchPolicy: 'cache-and-network',
    }),
    props: props => {
      const {
        data: { getCropForField },
      } = props;
      return { crop: getCropForField, ...props };
    },
  }),

  graphql(MUTATION_UPDATE_CROP, {
    options: () => ({
      onCompleted: ({ updateCropForField }) => {
        const newYield =
          updateCropForField.expectedYield[
            updateCropForField.expectedYield.length - 1
          ].yield.size;
        const lastYield =
          updateCropForField.expectedYield[
            updateCropForField.expectedYield.length - 2
          ].yield.size;

        if (updateCropForField) {
          mixpanel.track(ANALYTICS.EVENTS.YIELD.NEW_YIELD_LOGGED, {
            [ANALYTICS.PROPERTIES.CROP.NAME]: updateCropForField.crop.name,
            [ANALYTICS.PROPERTIES.CROP.PLANTED_AREA]:
              updateCropForField.plantedArea.size,
            [ANALYTICS.PROPERTIES.YIELD.YIELD_CHANGE]: Math.abs(
              lastYield - newYield
            ),
          });
        }

        return updateCropForField;
      },
    }),
    props: props => ({
      onUpdateCrop: (id, fieldCropUpdateInput) => {
        return props
          .mutate({
            variables: { id, fieldCropUpdateInput },
          })
          .then(res => res)
          .catch(graphQlToFormErrors);
      },
      ...props,
    }),
  }),

  connect(
    null,
    mapDispatch
  ),
  reduxForm(formSettings)
)(YieldForm);



// WEBPACK FOOTER //
// ./src/containers/YieldForm/index.js